import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { pluralize } from "../../pluralize";
import { describePlacementSetup, type CampaignDraft } from "./campaignDraft";
import type { SimulatorChoice } from "./goLiveViewModel";

interface GoLiveStepProps {
  draft: CampaignDraft;
  placements: PlacementCatalogEntry[];
  simulators: SimulatorChoice[];
  onSimulatorIdsChange: (ids: string[]) => void;
  onEditImages: () => void;
}

export function GoLiveStep({
  draft,
  placements,
  simulators,
  onSimulatorIdsChange,
  onEditImages,
}: GoLiveStepProps) {
  const selected = new Set(draft.simulatorIds);
  const online = simulators.filter((s) => s.isOnline);
  const offline = simulators.filter((s) => !s.isOnline);
  const toggle = (id: string) =>
    onSimulatorIdsChange(
      selected.has(id)
        ? draft.simulatorIds.filter((x) => x !== id)
        : [...draft.simulatorIds, id],
    );

  return (
    <div className="mt-6 flex flex-wrap items-start gap-5">
      <section className="flex-[1_1_300px] rounded-2xl border border-border bg-surface p-5">
        <h2 className="poppins text-base font-semibold text-ink">
          {draft.name.trim()}
        </h2>
        <p className="text-sm text-text-muted">
          {pluralize(placements.length, "placement")}
        </p>
        <dl className="mt-3.5 flex flex-col gap-2.5 text-sm">
          {placements.map((placement) => (
            <div key={placement.key} className="flex justify-between gap-2.5">
              <dt className="text-ink">{placement.name}</dt>
              <dd className="text-right text-text-muted">
                {describePlacementSetup(draft.setup[placement.key], placement)}
              </dd>
            </div>
          ))}
        </dl>
        <Button
          size="sm"
          variant="outline"
          className="mt-4 rounded-lg text-xs"
          onClick={onEditImages}
        >
          Edit images
        </Button>
      </section>

      <section className="flex-[999_1_520px] rounded-2xl border border-border bg-surface p-5">
        <h2 className="poppins text-base font-semibold text-ink">
          Where should it go live?
        </h2>
        <p className="mt-1 mb-3.5 text-sm text-text-muted">
          Each simulator shows one campaign at a time. Going live replaces
          what&rsquo;s there now. You can also save it as a draft and go live
          later.
        </p>

        {simulators.length === 0 ? (
          <p className="text-sm text-text-muted">
            This facility doesn&rsquo;t have any simulators yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {simulators.map((sim) => {
              const on = selected.has(sim.id);
              return (
                <button
                  key={sim.id}
                  type="button"
                  aria-pressed={on}
                  disabled={!sim.isOnline}
                  onClick={() => toggle(sim.id)}
                  className={cn(
                    "flex items-start gap-3 rounded-xl border-[1.5px] p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                    on ? "border-primary" : "border-border hover:border-ink/40",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-[1.5px]",
                      on
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border",
                    )}
                  >
                    {on && <Check className="size-3" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-ink">
                      {sim.name}
                      <span
                        className={cn(
                          "flex items-center gap-1 text-xs font-semibold",
                          sim.isOnline ? "text-good" : "text-destructive",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            "size-1.5 rounded-full",
                            sim.isOnline ? "bg-good" : "bg-destructive",
                          )}
                        />
                        {sim.isOnline ? "Online" : "Offline"}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs text-text-muted">
                      {sim.isOnline
                        ? sim.effect
                        : (sim.lastSeen ?? "Turn it on to include it.")}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg text-xs"
            disabled={online.length === 0}
            onClick={() => onSimulatorIdsChange(online.map((s) => s.id))}
          >
            Select all online
          </Button>
          <span className="font-semibold text-ink" aria-live="polite">
            {pluralize(draft.simulatorIds.length, "simulator")} selected
          </span>
          {offline.length > 0 && (
            <span className="text-text-muted">
              {offline.map((s) => s.name).join(", ")}{" "}
              {offline.length === 1 ? "is" : "are"} offline. Turn{" "}
              {offline.length === 1 ? "it" : "them"} on to include{" "}
              {offline.length === 1 ? "it" : "them"}.
            </span>
          )}
        </div>
      </section>
    </div>
  );
}
